// Riva: de mascotte van Curriva, los van het logo. Een zacht rond gezicht met
// een afstudeerhoedje. Het gezicht draagt de emotie; hoedje en kwastje hebben
// massa en bewegen met eigen traagheid na.

export const RIVA_MOODS = [
  "idle",
  "happy",
  "curious",
  "thinking",
  "working",
  "idea",
  "proud",
  "celebrate",
  "encourage",
  "surprised",
  "confused",
  "sad",
  "shy",
  "sleepy",
  "sleeping",
  "looking-away",
  "peeking",
] as const;
export type RivaMood = (typeof RIVA_MOODS)[number];

export const RIVA_LABELS: Record<RivaMood, string> = {
  idle: "Meekijken",
  happy: "Blij",
  curious: "Nieuwsgierig",
  thinking: "Nadenken",
  working: "Aan het werk",
  idea: "Idee!",
  proud: "Trots",
  celebrate: "Gelukt!",
  encourage: "Jij kunt dit",
  surprised: "Verrast",
  confused: "Even twijfelen",
  sad: "Verdrietig",
  shy: "Verlegen",
  sleepy: "Slaperig",
  sleeping: "Slaapt",
  "looking-away": "Ik kijk niet",
  peeking: "Stiekem kijken",
};

export type RivaGesture = "hop" | "wave" | "nod" | "wiggle";

/** Every value is spring-driven, so moods blend instead of snapping. */
export type Pose = {
  tilt: number;
  lift: number;
  /** Where the face points, -1 (left) … 1 (right). */
  turn: number;
  lookX: number;
  lookY: number;
  openL: number;
  openR: number;
  /** 0 = round eyes, 1 = smiling crescents. */
  happy: number;
  /** Closed eyes that curve downwards, as in sleep. */
  sag: number;
  browY: number;
  /** Positive raises the inner ends (worried), negative lowers them (focus). */
  browTilt: number;
  /** Positive raises the right brow and lowers the left. */
  browSkew: number;
  smile: number;
  mouthOpen: number;
  mouthWidth: number;
  mouthSkew: number;
  blush: number;
  /** 1 = Riva houdt zich stil: geen muis volgen, niet ademen, niet opzij schuiven. */
  hold: number;
  /** Rotation of the head alone, on top of the body tilt. */
  headTilt: number;
  sparkle: number;
  sleep: number;
  energy: number;
};

const base: Pose = {
  tilt: 0, lift: 0, turn: 0, lookX: 0, lookY: 0, openL: 1, openR: 1, happy: 0, sag: 0,
  browY: 0, browTilt: 0, browSkew: 0, smile: 0.45, mouthOpen: 0, mouthWidth: 13, mouthSkew: 0,
  blush: 0.35, hold: 0, headTilt: 0, sparkle: 0, sleep: 0, energy: 1,
};
const pose = (values: Partial<Pose>): Pose => ({ ...base, ...values });

export const POSES: Record<RivaMood, Pose> = {
  idle: pose({}),
  happy: pose({ happy: 0.6, smile: 0.95, mouthOpen: 0.28, mouthWidth: 15, blush: 0.65, lift: -2 }),
  curious: pose({ tilt: -9, turn: 0.25, lookX: 0.3, lookY: -0.1, openL: 1.08, openR: 1.14, browSkew: 4, smile: 0.2, mouthOpen: 0.18, mouthWidth: 8 }),
  thinking: pose({ tilt: 5, turn: 0.2, lookX: 0.6, lookY: -0.65, openL: 0.86, openR: 0.86, browSkew: -3, browTilt: -1, smile: 0, mouthWidth: 9, mouthSkew: 2, blush: 0.25 }),
  working: pose({ lookY: 0.4, openL: 0.74, openR: 0.74, browTilt: -3, browY: 1, smile: 0.12, mouthWidth: 8, blush: 0.25 }),
  idea: pose({ lift: -4, openL: 1.22, openR: 1.22, browY: -4, smile: 0.8, mouthOpen: 0.45, mouthWidth: 12, blush: 0.5, sparkle: 1 }),
  proud: pose({ tilt: -4, lift: -3, lookY: -0.2, happy: 1, smile: 0.85, mouthWidth: 14, blush: 0.55, sparkle: 0.65 }),
  celebrate: pose({ happy: 1, smile: 1, mouthOpen: 0.75, mouthWidth: 16, blush: 0.75, sparkle: 1 }),
  encourage: pose({ tilt: 6, happy: 0.35, smile: 0.78, mouthOpen: 0.1, mouthWidth: 14, blush: 0.5 }),
  surprised: pose({ lift: -3, openL: 1.3, openR: 1.3, browY: -6, smile: 0, mouthOpen: 0.9, mouthWidth: 7, blush: 0.3 }),
  confused: pose({ lookX: -0.3, lookY: -0.35, browSkew: -5, browTilt: 2, smile: -0.1, mouthWidth: 10, mouthSkew: 3.5, blush: 0.2 }),
  sad: pose({ lift: 5, lookY: 0.45, openL: 0.8, openR: 0.8, browTilt: 5, browY: 1, smile: -0.7, mouthWidth: 10, blush: 0.15, energy: 0.5 }),
  shy: pose({ tilt: -7, turn: -0.35, lookX: -0.6, lookY: 0.5, happy: 0.3, openL: 0.8, openR: 0.8, browTilt: 2, smile: 0.4, mouthWidth: 8, blush: 1 }),
  sleepy: pose({ lift: 3, openL: 0.34, openR: 0.34, browY: 2, smile: 0.1, mouthWidth: 8, energy: 0.6 }),
  sleeping: pose({ lift: 4, openL: 0.06, openR: 0.06, sag: 0.6, smile: 0.2, mouthWidth: 6, sleep: 1, energy: 0.9 }),
  // Verstoppen: het hoofd draait weg en knijpt de ogen dicht.
  "looking-away": pose({ hold: 1, headTilt: -14, turn: -0.9, lookX: -0.9, lookY: 0.25, openL: 0.06, openR: 0.06, sag: 0.3, browTilt: 1.5, smile: 0.25, mouthWidth: 8, blush: 0.6 }),
  peeking: pose({ hold: 1, headTilt: -8, turn: -0.45, lookX: 0.6, lookY: 0.1, openL: 0.06, openR: 0.95, browSkew: 3, smile: 0.5, mouthWidth: 10, mouthSkew: -1, blush: 0.5 }),
};

/** Short, quiet expressions that break up a long idle stretch. */
export const IDLE_GLANCES: { name: string; pose: Partial<Pose> }[] = [
  { name: "glance", pose: { lookX: -0.55, lookY: -0.3, turn: -0.2 } },
  { name: "smile", pose: { happy: 0.45, smile: 0.85, blush: 0.55 } },
  { name: "wonder", pose: { lookX: 0.5, lookY: -0.55, browSkew: 3, smile: 0.2, mouthWidth: 9 } },
  { name: "hum", pose: { tilt: 5, happy: 0.25, smile: 0.6, mouthOpen: 0.12, mouthWidth: 9 } },
];

/** Body language on top of the held pose, timed from the moment a mood starts. */
export type Beat = { tilt: number; lift: number; mouthOpen: number; openScale: number };
export const STILL_BEAT: Beat = { tilt: 0, lift: 0, mouthOpen: 0, openScale: 1 };

export function moodBeat(mood: RivaMood, age: number): Beat {
  const beat = { ...STILL_BEAT };
  const fade = (rate: number) => Math.exp(-age * rate);
  switch (mood) {
    case "happy":
      beat.lift = -Math.abs(Math.sin(age * 7)) * 3 * fade(1.6);
      break;
    case "thinking":
      beat.tilt = Math.sin(age * 1.2) * 2.5;
      break;
    // Aan het werk: geconcentreerd meewiegen.
    case "working":
      beat.tilt = Math.sin(age * 1.6) * 2;
      beat.lift = Math.sin(age * 3.2) * 1.2;
      break;
    case "celebrate": {
      const hop = Math.max(0, Math.sin((age * Math.PI * 2) / 1.05));
      beat.lift = -hop * hop * 10;
      beat.tilt = Math.sin(age * 3.1) * 4;
      break;
    }
    case "encourage":
      beat.lift = Math.sin(age * 6) * 2.2 * fade(1.2);
      break;
    case "confused":
      beat.tilt = Math.sin(age * 1.1) * 7;
      break;
    case "shy":
      beat.tilt = Math.sin(age * 1.5) * 1.5;
      break;
    case "sleepy": {
      const phase = age % 6;
      // A yawn, then the head slowly sinks and jerks back up.
      if (phase > 1.2 && phase < 2.8) beat.mouthOpen = Math.sin(((phase - 1.2) / 1.6) * Math.PI) * 0.75;
      if (phase > 1.2 && phase < 2.8) beat.openScale = 1 - Math.sin(((phase - 1.2) / 1.6) * Math.PI) * 0.8;
      if (phase > 4.2) beat.lift = Math.sin(((phase - 4.2) / 1.8) * Math.PI) * 6;
      break;
    }
  }
  // Keep body language modest.
  const calm = 0.7;
  beat.tilt *= calm;
  beat.lift *= calm;
  return beat;
}

// ── Geometry ────────────────────────────────────────────────────────────

/** Het midden van het gezicht. Alle maten zijn gezichtseenheden; het gezicht heeft straal 40. */
export const CENTER = { x: 128, y: 121 };
export const FACE_RADIUS = 40;
/** Face features are drawn for a radius of 55; slightly larger than proportional, so they stay readable when small. */
export const FEATURE_SCALE = 0.8;
/** De onderkant van het gezicht: daar staat Riva, en daaromheen kantelt het lijf. */
export const GROUND = CENTER.y + FACE_RADIUS;
export const INK = "#1f3150";
/** Het kader van Riva met hoedje: vierkant, het gezicht in het midden, ruimte voor hoedje en schaduw. */
export const VIEW = { x: 72, y: 56, size: 112 };

type Point = [number, number];
const f = (n: number) => Math.round(n * 100) / 100;

/** Eyes morph between round, smiling crescent and closed. Coordinates are local. */
export function eyePath(open: number, happy: number, sag: number) {
  const rx = 8, ry = 11;
  const o = Math.max(0.07, open);
  const top = ry * o - sag * ry * 0.55;
  const bottom = ry * o * (1 - 1.5 * happy) + sag * ry * 0.55;
  // Keep a visible line when the eye is closed.
  const b = Math.max(bottom, -top + 2.2);
  const k = 1.33;
  return `M${-rx} 0C${-rx} ${f(-top * k)} ${rx} ${f(-top * k)} ${rx} 0C${rx} ${f(b * k)} ${-rx} ${f(b * k)} ${-rx} 0Z`;
}

export function mouthPath(smile: number, open: number, width: number, skew: number) {
  const w = width / 2;
  const y = 19 - smile * 3.2;
  const up = 19 + smile * 2.2 - open * 5.5;
  const down = 19 + smile * 6.5 + open * 12;
  const inset = width * 0.04;
  return `M${f(-w)} ${f(y + skew)}C${f(-w + inset)} ${f(up)} ${f(w - inset)} ${f(up)} ${f(w)} ${f(y - skew)}C${f(w - inset)} ${f(down)} ${f(-w + inset)} ${f(down)} ${f(-w)} ${f(y + skew)}Z`;
}

// Sterretjes en z'tjes zweven om het gezicht; het kwastje hangt rechts, dus de z'tjes stijgen links op.
export const SPARKLES = [
  { x: 82, y: 74, size: 7, color: "#fd9420", phase: 0 },
  { x: 180, y: 66, size: 5, color: "#e82872", phase: 2.1 },
  { x: 76, y: 128, size: 5, color: "#01aef3", phase: 4.2 },
];
export const SPARKLE_PATH = "M0 -1Q0 0 1 0Q0 0 0 1Q0 0 -1 0Q0 0 0 -1Z";

// ── Afstudeerhoedje ─────────────────────────────────────────────────────

/** Kleuren van het hoedje: plank, rand, kapje en het kwastje (van knoop naar punt). */
export type HatStyle = "klassiek" | "kleur" | "blauw";
export const HAT_STYLES: Record<HatStyle, { top: [string, string]; edge: string; skull: [string, string]; tassel: [string, string, string] }> = {
  klassiek: { top: ["#2c4470", "#1f3150"], edge: "#132038", skull: ["#2a3f66", "#1a2a46"], tassel: ["#ffd23f", "#ffc414", "#f59e0b"] },
  kleur: { top: ["#2c4470", "#1f3150"], edge: "#132038", skull: ["#2a3f66", "#1a2a46"], tassel: ["#ff168f", "#fc4f4f", "#ffc414"] },
  blauw: { top: ["#07b4f4", "#1765ee"], edge: "#0f3fa8", skull: ["#2a7ff8", "#1650c8"], tassel: ["#ff168f", "#fc4f4f", "#ffc414"] },
};

/** Hoe het hoedje per stemming op het hoofd ligt: scheef (graden) en hoe ver het is afgezakt. */
export const HAT_POSES: Record<RivaMood, { roll: number; sink: number }> = {
  idle: { roll: 0, sink: 0 },
  happy: { roll: -3, sink: 0 },
  curious: { roll: -7, sink: 0 },
  thinking: { roll: 5, sink: 0 },
  working: { roll: 0, sink: 2.5 },
  idea: { roll: 0, sink: -3 },
  proud: { roll: -2, sink: -1.5 },
  celebrate: { roll: 0, sink: 0 },
  encourage: { roll: 3, sink: 0 },
  // Opgetrokken wenkbrauwen duwen het hoedje omhoog.
  surprised: { roll: 0, sink: -4.5 },
  confused: { roll: 11, sink: 1 },
  sad: { roll: -10, sink: 5 },
  shy: { roll: -6, sink: 6 },
  sleepy: { roll: 7, sink: 6 },
  // In slaap zakt het hoedje over één oog.
  sleeping: { roll: 15, sink: 12 },
  "looking-away": { roll: -6, sink: 3 },
  peeking: { roll: -4, sink: 2 },
};

/** Afmetingen in gezichtseenheden; de oorsprong is de kruin. */
export const HAT = { reach: 39, pitch: 0.34, lift: 7, thick: 3.2, skull: 42, yaw: 16, cord: 24 };

/** Dynamische stand van het hoedje: scheef, afgezakt, in de lucht en draaiing om de eigen as. */
export type HatState = { roll: number; sink: number; h: number; yaw: number };
export const HAT_REST: HatState = { roll: 0, sink: 0, h: 0, yaw: HAT.yaw };

type Matrix = [number, number, number, number, number, number];
const mul = (m: Matrix, n: Matrix): Matrix => [
  m[0] * n[0] + m[2] * n[1], m[1] * n[0] + m[3] * n[1], m[0] * n[2] + m[2] * n[3], m[1] * n[2] + m[3] * n[3],
  m[0] * n[4] + m[2] * n[5] + m[4], m[1] * n[4] + m[3] * n[5] + m[5],
];
const move = (x: number, y: number): Matrix => [1, 0, 0, 1, x, y];
const turnAround = (deg: number, cx = 0, cy = 0): Matrix => {
  const a = (deg * Math.PI) / 180, c = Math.cos(a), s = Math.sin(a);
  return mul(mul(move(cx, cy), [c, s, -s, c, 0, 0]), move(-cx, -cy));
};
export const applyMatrix = (m: Matrix, [x, y]: Point): Point => [m[0] * x + m[2] * y + m[4], m[1] * x + m[3] * y + m[5]];

function hull(points: Point[]) {
  const pts = [...points].sort((a, b) => a[0] - b[0] || a[1] - b[1]);
  const cross = (o: Point, a: Point, b: Point) => (a[0] - o[0]) * (b[1] - o[1]) - (a[1] - o[1]) * (b[0] - o[0]);
  const half = (list: Point[]) => {
    const out: Point[] = [];
    for (const p of list) { while (out.length >= 2 && cross(out[out.length - 2], out[out.length - 1], p) <= 0) out.pop(); out.push(p); }
    return out.slice(0, -1);
  };
  return [...half(pts), ...half([...pts].reverse())];
}
const polygon = (points: Point[]) => `M${points.map((p) => `${f(p[0])} ${f(p[1])}`).join("L")}Z`;

/** Het hoedje in eigen coördinaten: een vierkante plank in perspectief op een kapje. */
export function hatShape(yaw: number) {
  const corners = [0, 1, 2, 3].map((k): Point => {
    const a = ((yaw + k * 90) * Math.PI) / 180;
    return [Math.cos(a) * HAT.reach, -HAT.lift + Math.sin(a) * HAT.reach * HAT.pitch];
  });
  const r = HAT.skull, side = (50 * Math.PI) / 180;
  const sx = r * Math.sin(side), sy = FACE_RADIUS - r * Math.cos(side);
  return {
    skull: `M${f(-sx)} ${f(sy)}A${r} ${r} 0 0 1 ${f(sx)} ${f(sy)}Q0 ${f(sy + 7)} ${f(-sx)} ${f(sy)}Z`,
    edge: polygon(hull([...corners, ...corners.map(([x, y]): Point => [x, y + HAT.thick])])),
    board: polygon(corners),
    // Het koord loopt over de plank van het knoopje naar de hoek waar het kwastje hangt.
    cord: `M0 ${-HAT.lift}L${f(corners[0][0])} ${f(corners[0][1])}`,
    corner: corners[0],
  };
}

/** Plaatst het hoedje: draaien gebeurt om het midden van het hoofd, zodat het over de bol glijdt. */
export function hatMatrix(hat: HatState): Matrix {
  return mul(mul(move(CENTER.x, CENTER.y), turnAround(hat.roll)), move(0, -FACE_RADIUS + hat.sink - hat.h));
}

/** Het kwastje: een koord van de hoek naar de pluim, licht gebogen als het zwaait. */
export function tasselShape(anchor: Point, end: Point, bend = 0) {
  const dx = end[0] - anchor[0], dy = end[1] - anchor[1], len = Math.hypot(dx, dy) || 1;
  const mx = (anchor[0] + end[0]) / 2 - (dy / len) * bend, my = (anchor[1] + end[1]) / 2 + (dx / len) * bend;
  const angle = (Math.atan2(dy, dx) * 180) / Math.PI - 90;
  return { d: `M${f(anchor[0])} ${f(anchor[1])}Q${f(mx)} ${f(my)} ${f(end[0])} ${f(end[1])}`, tuft: `translate(${f(end[0])} ${f(end[1])}) rotate(${f(angle)})` };
}
export const TUFT_PATH = "M-1.9 0.6C-1.9 -1.8 1.9 -1.8 1.9 0.6L4.4 13Q0 15.6 -4.4 13Z";
/** Draadjes in de pluim, iets donkerder dan de pluim zelf. */
export const TUFT_STRANDS = "M-1.2 3L-2.4 13M0 3V14M1.2 3L2.4 13";

/** De pluim hangt recht onder de hoek: de stand zonder beweging. */
export const restingTassel = (anchor: Point): Point => [anchor[0], anchor[1] + HAT.cord];

// ── One frame ───────────────────────────────────────────────────────────

export type FrameInput = {
  pose: Pose;
  /** Horizontal position, rotation, lift and squash of the whole body. */
  x: number;
  squash: number;
  blink: number;
  t: number;
  hat?: HatState;
};

export function drawFrame({ pose: p, x, squash, blink, t, hat = HAT_REST }: FrameInput) {
  const sx = 1 + squash, sy = 1 - squash;
  const turn = p.turn;
  const spacing = 19 * (1 - Math.abs(turn) * 0.14);
  const fx = CENTER.x + turn * 9 + p.lookX * 2.5;
  const fy = CENTER.y - 5 + p.lookY * 2;
  const eyeOffset = { x: p.lookX * 4.5, y: p.lookY * 3.5 };
  const shut = 1 - 0.93 * blink;
  const eyes = [
    { x: -spacing, open: p.openL * shut, scale: 1 - Math.max(0, turn) * 0.12 },
    { x: spacing, open: p.openR * shut, scale: 1 + Math.min(0, turn) * 0.12 },
  ].map((eye) => ({
    transform: `translate(${f(fx + eye.x + eyeOffset.x)} ${f(fy + eyeOffset.y + (1 - Math.min(1, eye.open)) * 3)}) scale(${f(eye.scale)})`,
    d: eyePath(eye.open, p.happy, p.sag),
    shine: f(Math.max(0, Math.min(1, (eye.open - 0.45) * 2)) * (1 - p.happy)),
  }));
  const brows = [-1, 1].map((side) => {
    const bx = fx + side * spacing + eyeOffset.x * 0.5;
    const by = fy - 20 + p.browY - side * p.browSkew - Math.max(0, p.openL - 1) * 6;
    return `translate(${f(bx)} ${f(by)}) rotate(${f(side * p.browTilt * 3.2)})`;
  });
  const breathe = Math.sin(t * (p.sleep > 0.5 ? 1.1 : 1.9));
  // Dezelfde transformaties als body en head, als matrix: zo weet het kwastje waar zijn hoek in de wereld is.
  const pivot = GROUND;
  const bodyM = mul(mul(mul(move(x, p.lift), turnAround(p.tilt, CENTER.x, pivot)), mul(move(CENTER.x, pivot), [sx, 0, 0, sy, 0, 0])), move(-CENTER.x, -pivot));
  const headM = mul(bodyM, mul(turnAround(p.headTilt, CENTER.x, CENTER.y + 10), move(turn * 1.5, Math.abs(p.headTilt) * 0.12)));
  const hatM = hatMatrix(hat);
  const hatShapes = hatShape(hat.yaw);
  return {
    body: `translate(${f(x)} ${f(p.lift)}) rotate(${f(p.tilt)} ${CENTER.x} ${GROUND}) translate(${CENTER.x} ${GROUND}) scale(${f(sx)} ${f(sy)}) translate(${-CENTER.x} ${-GROUND})`,
    // De schaduw krimpt en vervaagt als Riva van de grond komt.
    shadow: { rx: f(Math.max(14, 27 + p.lift * 0.5)), opacity: f(Math.max(0.04, 0.13 + p.lift * 0.005)) },
    head: `rotate(${f(p.headTilt)} ${CENTER.x} ${CENTER.y + 10}) translate(${f(turn * 1.5)} ${f(Math.abs(p.headTilt) * 0.12)})`,
    highlight: { cx: f(0.38 - turn * 0.12), cy: f(0.3) },
    eyes,
    brows,
    mouth: { transform: `translate(${f(fx + p.lookX * 1.5)} ${f(fy)})`, d: mouthPath(p.smile, p.mouthOpen, p.mouthWidth, p.mouthSkew) },
    tongue: { cy: f(19 + p.smile * 4 + p.mouthOpen * 9), rx: f(p.mouthWidth * 0.34), ry: f(Math.max(0, p.mouthOpen) * 4.5), opacity: f(Math.max(0, Math.min(1, p.mouthOpen * 2))) },
    cheeks: { opacity: f(0.1 + p.blush * 0.34), left: `translate(${f(fx - 31 - turn * 3)} ${f(fy + 12)})`, right: `translate(${f(fx + 31 - turn * 3)} ${f(fy + 12)})` },
    sparkles: SPARKLES.map((s) => {
      const twinkle = 0.65 + 0.35 * Math.sin(t * 4.2 + s.phase);
      const scale = s.size * p.sparkle * twinkle;
      return { transform: `translate(${s.x} ${s.y}) rotate(${f(t * 40 + s.phase * 30)}) scale(${f(Math.max(0.001, scale))})`, opacity: f(Math.min(1, p.sparkle * 1.4)) };
    }),
    zzz: [0, 1].map((i) => {
      const phase = (t * 0.45 + i * 0.5) % 1;
      return { transform: `translate(${f(88 - i * 9 - phase * 6)} ${f(96 - i * 12 - phase * 18)})`, opacity: f(p.sleep * Math.sin(phase * Math.PI)) };
    }),
    breathe,
    hat: {
      ...hatShapes,
      transform: `matrix(${hatM.map(f).join(" ")})`,
      anchor: applyMatrix(mul(headM, hatM), hatShapes.corner),
    },
  };
}
export type Frame = ReturnType<typeof drawFrame>;
